import type { createBrowserRouter } from 'react-router'
import { RedirectLoopError } from '../error'
import { createWeakMap } from '../map'

interface NavigationRecord {
  pathname: string
  timestamp: number
}

/** Rule to detect redirect loops */
export interface RedirectLoopRule {
  /** Time window in ms */
  duration: number
  /** Max visits allowed within the duration. Exceeding this triggers the rule. */
  maxCount: number
}

export interface DetectRouterNavigationsOptions {
  /** Maximum number of navigation records to keep. Default: 100 */
  maxHistorySize?: number
  /** Rules to detect redirect loops. Any rule matched will trigger an error. */
  rules: RedirectLoopRule[]
  /** Callback when a loop is detected */
  onLoopDetected?: (error: RedirectLoopError) => void
}

/**
 * Checks if any path appears more than allowed times within the rule's time window.
 */
function detectLoop(
  history: NavigationRecord[],
  rule: RedirectLoopRule,
): RedirectLoopError | null {
  const cutoff = Date.now() - rule.duration

  // Find first record within the time window (history is sorted by timestamp ascending)
  const startIndex = history.findIndex(r => r.timestamp > cutoff)
  if (startIndex === -1) return null

  // Count occurrences of each path from startIndex
  const pathCounts = new Map<string, number>()
  for (let i = startIndex; i < history.length; i++) {
    const record = history[i]
    const count = (pathCounts.get(record.pathname) ?? 0) + 1
    pathCounts.set(record.pathname, count)

    if (count > rule.maxCount) {
      return new RedirectLoopError(record.pathname)
    }
  }

  return null
}

type Router = ReturnType<typeof createBrowserRouter>

// Per-router isolated history, auto-cleaned when router is garbage collected
const routerHistories = createWeakMap<Router, NavigationRecord[]>()

// Module-level flag to stop monitoring after a loop is detected
let loopDetected = false

/**
 * Tracks router navigations and detects redirect loops that could cause
 * the application to freeze or become unresponsive.
 *
 * @param router - The browser router instance
 * @param options - Configuration options
 */
export function detectRouterNavigations(
  router: Router,
  options: DetectRouterNavigationsOptions,
) {
  const { maxHistorySize = 100, rules, onLoopDetected } = options

  let history = routerHistories.get(router)
  if (!history) {
    history = []
    routerHistories.set(router, history)
  }

  router.subscribe(routerState => {
    // Once a loop is detected, stop all monitoring
    if (loopDetected) return

    const { pathname } = routerState.location
    const now = Date.now()

    // Add to history with ring buffer behavior
    if (history.length >= maxHistorySize) {
      history.shift()
    }
    history.push({ pathname, timestamp: now })

    // Detect loops - check each rule
    for (const rule of rules) {
      const error = detectLoop(history, rule)
      if (!error) continue
      // Set flag to prevent further monitoring
      loopDetected = true

      // Call the optional callback
      onLoopDetected?.(error)

      // Use hard redirect to break the loop completely.
      // router.navigate won't work as the redirect loop will override it.
      window.location.replace(
        `/error?code=redirect-loop&pathname=${encodeURIComponent(error.pathname)}&report=1`,
      )

      // Throw error synchronously to halt any further execution in this tick
      throw error
    }
  })
}
